import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Input, Label, Card } from '@caruso/ui';
import { Loader2, CheckCircle2, XCircle, FileText, Clock } from 'lucide-react';
import { api } from '@/lib/axios';

interface FormField {
  id: string;
  label: string;
  variableName?: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'textarea';
  required: boolean;
  options?: string[];
  placeholder?: string;
}

interface FormData {
  id: string;
  token: string;
  client: {
    id: string;
    name: string | null;
    email: string | null;
  };
  tenant: {
    id: string;
    name: string;
  };
  formData: {
    title: string;
    description?: string;
    fields: FormField[];
    submitButtonText?: string;
  };
  expiresAt: string | null;
}

export default function PublicFormPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) return;

    const fetchForm = async () => {
      try {
        const response = await api.get(`/forms/${token}`);
        setFormData(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao carregar formulário');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [token]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    formData?.formData.fields.forEach((field) => {
      if (field.required && !responses[field.id]) {
        errors[field.id] = 'Este campo é obrigatório';
      }

      // Validações específicas por tipo
      if (responses[field.id]) {
        if (field.type === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(responses[field.id])) {
            errors[field.id] = 'Email inválido';
          }
        }

        if (field.type === 'phone') {
          const phoneRegex = /^\d{10,11}$/;
          const cleaned = responses[field.id].replace(/\D/g, '');
          if (!phoneRegex.test(cleaned)) {
            errors[field.id] = 'Telefone inválido (10-11 dígitos)';
          }
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.post(`/forms/${token}/submit`, { responses });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao enviar formulário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setResponses((prev) => ({ ...prev, [fieldId]: value }));
    // Limpar erro de validação quando o usuário começar a digitar
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.id,
      value: responses[field.id] || '',
      onChange: (e: any) => handleFieldChange(field.id, e.target.value),
      placeholder: field.placeholder,
      className: `bg-brand-ink/50 border-brand-gold/20 text-brand-ivory ${
        validationErrors[field.id] ? 'border-red-500' : ''
      }`,
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={4}
            className={`w-full ${commonProps.className} rounded px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold/50`}
          />
        );

      case 'select':
        return (
          <select
            {...commonProps}
            className={`w-full ${commonProps.className} rounded px-3 py-2 h-10 focus:outline-none focus:ring-2 focus:ring-brand-gold/50`}
          >
            <option value="">Selecione uma opção</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'date':
        return <Input {...commonProps} type="date" />;

      case 'number':
        return <Input {...commonProps} type="number" />;

      case 'email':
        return <Input {...commonProps} type="email" />;

      case 'phone':
        return (
          <Input
            {...commonProps}
            type="tel"
            placeholder={field.placeholder || '(00) 00000-0000'}
          />
        );

      default:
        return <Input {...commonProps} type="text" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-brand-gold" />
          <p className="text-brand-ivory/60">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="bg-brand-ink border-brand-gold/20 p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-serif text-brand-gold mb-2">Erro</h2>
          <p className="text-brand-ivory/60 mb-6">{error}</p>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="bg-brand-ink border-brand-gold/20 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-serif text-brand-gold mb-2">Formulário Enviado!</h2>
          <p className="text-brand-ivory/60 mb-6">
            Obrigado por preencher o formulário. Suas respostas foram registradas com sucesso.
          </p>
        </Card>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className="min-h-screen bg-brand-ink">
      {/* Header */}
      <header className="bg-brand-ink border-b border-brand-gold/20 sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <h1 className="text-2xl font-serif text-brand-gold">
              {formData.tenant.name}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-brand-ink border-brand-gold/20 p-6 sm:p-8">
          {/* Form Header */}
          <div className="flex items-start gap-4 mb-6 pb-6 border-b border-brand-gold/20">
            <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-brand-gold" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-serif text-brand-gold mb-2">
                {formData.formData.title}
              </h2>
              {formData.formData.description && (
                <p className="text-brand-ivory/60 text-sm">
                  {formData.formData.description}
                </p>
              )}
            </div>
          </div>

          {/* Client Info */}
          {formData.client.name && (
            <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-brand-ivory">
                <span className="font-medium">Olá, {formData.client.name}!</span>
                {' '}Por favor, preencha o formulário abaixo.
              </p>
            </div>
          )}

          {/* Expiration Warning */}
          {formData.expiresAt && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-yellow-400">
                <Clock className="w-4 h-4" />
                <span>
                  Este formulário expira em:{' '}
                  <span className="font-medium">
                    {new Date(formData.expiresAt).toLocaleString('pt-BR')}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Form Fields */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {formData.formData.fields.map((field) => (
              <div key={field.id}>
                <Label htmlFor={field.id} className="text-brand-ivory/80">
                  {field.label}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                <div className="mt-1.5">
                  {renderField(field)}
                </div>
                {validationErrors[field.id] && (
                  <p className="text-red-400 text-sm mt-1">{validationErrors[field.id]}</p>
                )}
              </div>
            ))}

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-gold hover:bg-brand-gold/90 text-brand-dark font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                formData.formData.submitButtonText || 'Enviar'
              )}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
